/**
 * Plan de cuentas del comité (DISENO_CONTABILIDAD §5 y §7.6).
 *
 * Las cuentas nacen de las cargas con una clasificación propuesta por
 * prefijo; el contador la corrige acá. Tipo decide en qué columnas del
 * balance cae la cuenta; rubro, en qué indicador del Inicio suma.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";

import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { hasPermission } from "../../lib/auth";
import { cantidad } from "../../lib/format";
import { useAuth } from "../auth/useAuth";
import {
  actualizarCuenta,
  fetchCuentas,
  fetchGrupos,
  fetchRubros,
  nombrarGrupo,
  type Cuenta,
  type GrupoCuenta,
  type Rubro,
  type TipoCuenta,
} from "./api";

const TIPOS: { valor: TipoCuenta; texto: string }[] = [
  { valor: "activo", texto: "Activo" },
  { valor: "pasivo", texto: "Pasivo" },
  { valor: "patrimonio", texto: "Patrimonio" },
  { valor: "ingreso", texto: "Ingreso" },
  { valor: "gasto", texto: "Gasto" },
];

type Filtro = "todas" | "por_revisar" | "sin_clasificar";
type Vista = "cuentas" | "grupos";

/** Nombre de un grupo: se guarda al salir del campo o con Enter. */
function FilaGrupo({
  grupo,
  editable,
}: {
  grupo: GrupoCuenta;
  editable: boolean;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [valor, setValor] = useState(grupo.nombre ?? "");
  const m = useMutation({
    mutationFn: (nombre: string) =>
      nombrarGrupo(grupo.codigo, nombre.trim() || null),
    onSuccess: (nuevo) => {
      qc.setQueryData<GrupoCuenta[]>(["contabilidad", "grupos"], (prev) =>
        prev?.map((g) => (g.codigo === nuevo.codigo ? nuevo : g)),
      );
      void qc.invalidateQueries({ queryKey: ["contabilidad", "balance"] });
    },
    onError: (e) => {
      setValor(grupo.nombre ?? "");
      toast.error(e instanceof Error ? e.message : "No se pudo guardar");
    },
  });
  const guardar = (): void => {
    if (valor.trim() !== (grupo.nombre ?? "")) m.mutate(valor);
  };

  return (
    <tr className="hover:bg-neutral-50">
      <td
        className="whitespace-nowrap px-3 py-1.5 tabular-nums text-neutral-600"
        style={{ paddingLeft: `${0.75 + (grupo.nivel - 1)}rem` }}
      >
        {grupo.codigo}
      </td>
      <td className="px-3 py-1.5 text-neutral-600">Nivel {grupo.nivel}</td>
      <td className="px-3 py-1.5">
        {editable ? (
          <input
            aria-label={`Nombre del grupo ${grupo.codigo}`}
            value={valor}
            maxLength={200}
            placeholder="Sin nombre"
            disabled={m.isPending}
            onChange={(e) => setValor(e.target.value)}
            onBlur={guardar}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") setValor(grupo.nombre ?? "");
            }}
            className="w-full rounded-md border-neutral-450 py-1 text-sm focus-visible:ring-2 focus-visible:ring-brand-500"
          />
        ) : (
          (grupo.nombre ?? <span className="text-neutral-500">Sin nombre</span>)
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-neutral-600">
        {cantidad(grupo.cuentas)}
      </td>
    </tr>
  );
}

function TablaGrupos({
  editable,
  texto,
}: {
  editable: boolean;
  texto: string;
}): JSX.Element {
  const grupos = useQuery({
    queryKey: ["contabilidad", "grupos"],
    queryFn: fetchGrupos,
  });
  const t = texto.trim().toLowerCase();
  const visibles = (grupos.data ?? []).filter(
    (g) =>
      !t || g.codigo.includes(t) || (g.nombre ?? "").toLowerCase().includes(t),
  );
  if (grupos.isLoading) return <Cargando />;
  if (grupos.isError) return <ErrorCarga error={grupos.error} onReintentar={() => void grupos.refetch()} />;
  return (
    <TableWrap label="Grupos del plan de cuentas">
      <table className="w-full min-w-[640px] text-sm">
        <caption className="sr-only">Grupos del plan de cuentas</caption>
        <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
          <tr>
            <th className="w-48 px-3 py-2 text-left font-medium">Código</th>
            <th className="w-24 px-3 py-2 text-left font-medium">Nivel</th>
            <th className="px-3 py-2 text-left font-medium">Nombre</th>
            <th className="w-24 px-3 py-2 text-right font-medium">Cuentas</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-200">
          {visibles.length === 0 && (
            <tr>
              <td
                colSpan={4}
                className="px-3 py-6 text-center text-neutral-600"
              >
                {(grupos.data ?? []).length === 0
                  ? "Todavía no hay grupos: nacen de las cuentas al subir el libro diario."
                  : "Ningún grupo calza con la búsqueda."}
              </td>
            </tr>
          )}
          {visibles.map((g) => (
            <FilaGrupo key={g.codigo} grupo={g} editable={editable} />
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function Fila({
  cuenta,
  rubros,
  editable,
}: {
  cuenta: Cuenta;
  rubros: Rubro[];
  editable: boolean;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (body: Parameters<typeof actualizarCuenta>[1]) =>
      actualizarCuenta(cuenta.id, body),
    onSuccess: (nueva) => {
      qc.setQueryData<Cuenta[]>(["contabilidad", "cuentas"], (prev) =>
        prev?.map((c) => (c.id === nueva.id ? nueva : c)),
      );
      void qc.invalidateQueries({ queryKey: ["contabilidad", "resumen"] });
      void qc.invalidateQueries({ queryKey: ["contabilidad", "balance"] });
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "No se pudo guardar"),
  });
  const rubrosDelTipo = rubros.filter((r) => r.tipo === cuenta.tipo);
  const tituloRubro = rubros.find((r) => r.codigo === cuenta.rubro)?.titulo;
  const idBase = `cuenta-${cuenta.id}`;

  return (
    <tr className="hover:bg-neutral-50">
      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-neutral-600">
        {cuenta.codigo}
      </td>
      <td className="px-3 py-1.5 text-neutral-900">{cuenta.nombre}</td>
      <td className="px-3 py-1.5">
        {editable ? (
          <select
            id={`${idBase}-tipo`}
            aria-label={`Tipo de ${cuenta.nombre}`}
            value={cuenta.tipo ?? ""}
            disabled={m.isPending}
            onChange={(e) =>
              m.mutate({
                tipo: (e.target.value || null) as TipoCuenta | null,
                rubro: null,
              })
            }
            className="w-full rounded-md border-neutral-450 py-1 text-sm focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <option value="">Sin clasificar</option>
            {TIPOS.map((t) => (
              <option key={t.valor} value={t.valor}>
                {t.texto}
              </option>
            ))}
          </select>
        ) : (
          (TIPOS.find((t) => t.valor === cuenta.tipo)?.texto ??
          "Sin clasificar")
        )}
      </td>
      <td className="px-3 py-1.5">
        {editable ? (
          <select
            id={`${idBase}-rubro`}
            aria-label={`Rubro de ${cuenta.nombre}`}
            value={cuenta.rubro ?? ""}
            disabled={m.isPending || !cuenta.tipo}
            onChange={(e) =>
              m.mutate({ tipo: cuenta.tipo, rubro: e.target.value || null })
            }
            className="w-full rounded-md border-neutral-450 py-1 text-sm focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-neutral-100"
          >
            <option value="">Sin rubro</option>
            {rubrosDelTipo.map((r) => (
              <option key={r.codigo} value={r.codigo}>
                {r.titulo}
              </option>
            ))}
          </select>
        ) : (
          (tituloRubro ?? "—")
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {cuenta.revisada ? (
          <Badge tone="emerald">Revisada</Badge>
        ) : editable ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => m.mutate({ revisada: true })}
            disabled={m.isPending}
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" /> Confirmar
          </Button>
        ) : (
          <Badge tone="amber">Por revisar</Badge>
        )}
      </td>
    </tr>
  );
}

export function PlanCuentasPage(): JSX.Element {
  const { state } = useAuth();
  const editable = hasPermission(state, "contabilidad.plan");
  const [params, setParams] = useSearchParams();
  const vista = (params.get("vista") as Vista | null) ?? "cuentas";
  const filtro = (params.get("filtro") as Filtro | null) ?? "todas";
  const texto = params.get("q") ?? "";
  const cuentas = useQuery({
    queryKey: ["contabilidad", "cuentas"],
    queryFn: fetchCuentas,
  });
  const rubros = useQuery({
    queryKey: ["contabilidad", "rubros"],
    queryFn: fetchRubros,
  });

  const setParam = (k: string, v: string): void => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const visibles = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return (cuentas.data ?? []).filter((c) => {
      if (filtro === "por_revisar" && c.revisada) return false;
      if (filtro === "sin_clasificar" && c.tipo) return false;
      return !t || c.codigo.includes(t) || c.nombre.toLowerCase().includes(t);
    });
  }, [cuentas.data, filtro, texto]);
  const porRevisar = (cuentas.data ?? []).filter((c) => !c.revisada).length;
  const sinClasificar = (cuentas.data ?? []).filter((c) => !c.tipo).length;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <PageHeader
        title="Plan de cuentas"
        subtitle={
          <>
            El tipo decide en qué columnas del balance cae cada cuenta; el rubro,
            en qué indicador del Inicio suma. Las cuentas nuevas llegan con una
            clasificación propuesta según su código. En <strong>Grupos</strong> se
            nombran los niveles superiores que muestra el balance agrupado.
          </>
        }
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            label="Ver"
            value={vista}
            onChange={(e) =>
              setParam(
                "vista",
                e.target.value === "cuentas" ? "" : e.target.value,
              )
            }
          >
            <option value="cuentas">Cuentas</option>
            <option value="grupos">Grupos</option>
          </Select>
        </div>
        {vista === "cuentas" && (
          <div className="w-56">
            <Select
              label="Mostrar"
              value={filtro}
              onChange={(e) =>
                setParam(
                  "filtro",
                  e.target.value === "todas" ? "" : e.target.value,
                )
              }
            >
              <option value="todas">
                Todas ({cantidad(cuentas.data?.length ?? 0)})
              </option>
              <option value="por_revisar">
                Por revisar ({cantidad(porRevisar)})
              </option>
              <option value="sin_clasificar">
                Sin clasificar ({cantidad(sinClasificar)})
              </option>
            </Select>
          </div>
        )}
        <div className="w-72">
          <Input
            label="Buscar"
            placeholder="Código o nombre"
            value={texto}
            onChange={(e) => setParam("q", e.target.value)}
          />
        </div>
      </div>

      {vista === "grupos" && <TablaGrupos editable={editable} texto={texto} />}
      {vista === "cuentas" && (cuentas.isLoading || rubros.isLoading) && (
        <Cargando />
      )}
      {vista === "cuentas" && cuentas.isError && (
        <ErrorCarga error={cuentas.error} onReintentar={() => void cuentas.refetch()} />
      )}
      {vista === "cuentas" && cuentas.data && rubros.data && (
        <TableWrap label="Plan de cuentas">
          <table className="w-full min-w-[860px] text-sm">
            <caption className="sr-only">Plan de cuentas</caption>
            <thead className="bg-neutral-100 text-xs uppercase tracking-wide text-neutral-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Código</th>
                <th className="px-3 py-2 text-left font-medium">Cuenta</th>
                <th className="w-40 px-3 py-2 text-left font-medium">Tipo</th>
                <th className="w-64 px-3 py-2 text-left font-medium">Rubro</th>
                <th className="w-32 px-3 py-2 text-left font-medium">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {visibles.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-6 text-center text-neutral-600"
                  >
                    {cuentas.data.length === 0
                      ? "Todavía no hay cuentas: nacen al subir el libro diario."
                      : "Ninguna cuenta calza con el filtro."}
                  </td>
                </tr>
              )}
              {visibles.map((c) => (
                <Fila
                  key={c.id}
                  cuenta={c}
                  rubros={rubros.data}
                  editable={editable}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}
